/**
 * Shared oscillator zone tokens (spec §11.1 & §11.6).
 *
 * Single source of truth for the zone label and color mapping used by the
 * global banner, the Market detail page, and the deployment-plan strip. Import
 * from this module in every surfacing point so the scale never diverges.
 *
 * Palette uses red / amber / slate / teal / emerald — colorblind-safe
 * (avoids a pure red/green pair).
 */

export const ZONE_KEYS = Object.freeze([
  'extreme_overbought',
  'overbought',
  'getting_extended',
  'neutral',
  'getting_oversold',
  'oversold',
  'extreme_oversold',
]);

const ZONE_DEFS = {
  extreme_overbought: {
    label: 'Extreme overbought',
    short: 'EXT OB',
    tone: 'Stand down',
    dotClass: 'bg-red-600',
    textClass: 'text-red-400',
    bgSoft: 'bg-red-500/15',
    borderClass: 'border-red-500/40',
    ringClass: 'ring-red-500/40',
    stroke: '#dc2626',
    fill: 'rgba(220, 38, 38, 0.18)',
  },
  overbought: {
    label: 'Overbought',
    short: 'OB',
    tone: 'Caution',
    dotClass: 'bg-red-500',
    textClass: 'text-red-300',
    bgSoft: 'bg-red-500/10',
    borderClass: 'border-red-500/30',
    ringClass: 'ring-red-500/30',
    stroke: '#ef4444',
    fill: 'rgba(239, 68, 68, 0.14)',
  },
  getting_extended: {
    label: 'Getting extended',
    short: 'EXT',
    tone: 'Mild caution',
    dotClass: 'bg-amber-500',
    textClass: 'text-amber-300',
    bgSoft: 'bg-amber-500/10',
    borderClass: 'border-amber-500/30',
    ringClass: 'ring-amber-500/30',
    stroke: '#f59e0b',
    fill: 'rgba(245, 158, 11, 0.14)',
  },
  neutral: {
    label: 'Neutral',
    short: 'NEUT',
    tone: 'No signal',
    dotClass: 'bg-slate-500',
    textClass: 'text-slate-300',
    bgSoft: 'bg-slate-500/10',
    borderClass: 'border-slate-500/30',
    ringClass: 'ring-slate-500/30',
    stroke: '#64748b',
    fill: 'rgba(100, 116, 139, 0.12)',
  },
  getting_oversold: {
    label: 'Getting oversold',
    short: 'OS-',
    tone: 'Opportunity',
    dotClass: 'bg-teal-400',
    textClass: 'text-teal-300',
    bgSoft: 'bg-teal-500/10',
    borderClass: 'border-teal-500/30',
    ringClass: 'ring-teal-500/30',
    stroke: '#2dd4bf',
    fill: 'rgba(45, 212, 191, 0.14)',
  },
  oversold: {
    label: 'Oversold',
    short: 'OS',
    tone: 'Deployable',
    dotClass: 'bg-emerald-500',
    textClass: 'text-emerald-300',
    bgSoft: 'bg-emerald-500/15',
    borderClass: 'border-emerald-500/40',
    ringClass: 'ring-emerald-500/40',
    stroke: '#10b981',
    fill: 'rgba(16, 185, 129, 0.16)',
  },
  extreme_oversold: {
    label: 'Extreme oversold',
    short: 'EXT OS',
    tone: 'High conviction',
    dotClass: 'bg-emerald-600',
    textClass: 'text-emerald-200',
    bgSoft: 'bg-emerald-500/20',
    borderClass: 'border-emerald-500/50',
    ringClass: 'ring-emerald-500/50',
    stroke: '#059669',
    fill: 'rgba(5, 150, 105, 0.22)',
  },
};

const UNKNOWN_TOKEN = {
  label: 'Unknown',
  short: '—',
  tone: '',
  dotClass: 'bg-slate-600',
  textClass: 'text-slate-400',
  bgSoft: 'bg-slate-500/5',
  borderClass: 'border-slate-600/30',
  ringClass: 'ring-slate-600/30',
  stroke: '#475569',
  fill: 'rgba(71, 85, 105, 0.10)',
};

/**
 * Classify a numeric oscillator value into a zone key.
 *
 * Boundaries mirror `backend/services/oscillator._classify_zone` so the
 * label rendered by the dashboard always matches the `zone` field persisted
 * on each reading: ±3 → "mild", ±5 → "strong", ±7 → "extreme".
 *
 *   >=  7              → extreme_overbought
 *   >=  5 and <  7     → overbought
 *   >=  3 and <  5     → getting_extended
 *   >  -3 and <  3     → neutral
 *   >  -5 and <= -3    → getting_oversold
 *   >  -7 and <= -5    → oversold
 *   <= -7              → extreme_oversold
 */
export function zoneForValue(value) {
  if (value == null || Number.isNaN(Number(value))) return 'neutral';
  const v = Number(value);
  if (v >= 7) return 'extreme_overbought';
  if (v >= 5) return 'overbought';
  if (v >= 3) return 'getting_extended';
  if (v > -3) return 'neutral';
  if (v > -5) return 'getting_oversold';
  if (v > -7) return 'oversold';
  return 'extreme_oversold';
}

/**
 * Map the backend's persisted zone names (§3.4) to our UI zone keys.
 * Used when we want to honor the server-classified zone directly.
 */
const BACKEND_ZONE_MAP = {
  extreme_positive: 'extreme_overbought',
  strong_positive: 'overbought',
  mild_positive: 'getting_extended',
  neutral: 'neutral',
  mild_negative: 'getting_oversold',
  strong_negative: 'oversold',
  extreme_negative: 'extreme_oversold',
};

/**
 * Canonicalize a zone key — accepts either the UI keys or the backend's
 * `*_positive / *_negative` names and returns a UI key. Falls back to
 * `zoneForValue(value)` if the label is unknown.
 */
export function canonicalZone(zoneOrNull, value) {
  if (zoneOrNull && BACKEND_ZONE_MAP[zoneOrNull]) return BACKEND_ZONE_MAP[zoneOrNull];
  if (zoneOrNull && ZONE_KEYS.includes(zoneOrNull)) return zoneOrNull;
  return zoneForValue(value);
}

export function zoneLabel(zone) {
  return (ZONE_DEFS[zone] || UNKNOWN_TOKEN).label;
}

export function zoneTokens(zone) {
  return ZONE_DEFS[zone] || UNKNOWN_TOKEN;
}

/**
 * Derive the deployment-plan strip label (§11.3).
 *
 *   osc <= -3  AND eligible exists → FAVORABLE
 *   osc neutral                    → NEUTRAL
 *   osc >= +3  AND eligible exists → CAUTION
 *   osc >= +5  AND eligible exists → UNFAVORABLE
 */
export function deploymentLabel(value, hasEligible) {
  if (value == null || Number.isNaN(Number(value))) {
    return { label: 'NEUTRAL', tone: 'neutral' };
  }
  const v = Number(value);
  const eligible = Boolean(hasEligible);
  if (eligible && v >= 5) return { label: 'UNFAVORABLE', tone: 'overbought' };
  if (eligible && v >= 3) return { label: 'CAUTION', tone: 'getting_extended' };
  if (eligible && v <= -3) return { label: 'FAVORABLE', tone: 'oversold' };
  return { label: 'NEUTRAL', tone: 'neutral' };
}

/**
 * Per-tranche badge for eligible rows (§11.3).
 *
 *   osc <= -3 AND within 2% of ceiling → 'supports'
 *   osc >= +5                          → 'stretched'
 *   else                               → 'neutral'
 */
export function trancheBadge(value, withinCeilingPct = null) {
  if (value == null || Number.isNaN(Number(value))) {
    return { key: 'neutral', label: 'Neutral', tone: 'neutral' };
  }
  const v = Number(value);
  const near = withinCeilingPct != null && Math.abs(withinCeilingPct) <= 2;
  if (v <= -3 && near) {
    return {
      key: 'supports',
      label: 'Oversold supports',
      tone: 'oversold',
    };
  }
  if (v >= 5) {
    return {
      key: 'stretched',
      label: 'Stretched — consider skip',
      tone: 'overbought',
    };
  }
  return { key: 'neutral', label: 'Neutral', tone: 'neutral' };
}

/** Direction arrow glyph for a `direction_change` signal. */
export function directionGlyph(direction) {
  if (direction === 'up') return '↑';
  if (direction === 'down') return '↓';
  if (direction === 'flat') return '→';
  return '·';
}
